/**
 * ProjectForm Component
 * Dynamic project creation/edit form with domain-specific metadata
 * Used by: agencies, construction, architects, consulting, IT, photographers, events
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CompanyType } from '@/types/company';
import { IndustryProject, CreateProjectInput, ProjectStatus } from '@/types/industry';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Form schema with validation
const projectFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  description: z.string().optional(),
  client_id: z.number().optional(),
  status: z.enum(['planning', 'active', 'on_hold', 'completed', 'cancelled']).default('planning'),
  budget_revenue: z.number().min(0).optional(),
  budget_cost: z.number().min(0).optional(),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
  // Metadata fields handled separately for type safety
});

type ProjectFormData = z.infer<typeof projectFormSchema>;

interface ProjectFormProps {
  domain: CompanyType;
  project?: IndustryProject; // For edit mode
  clients?: Array<{ id: number; name: string }>; // Available clients
  onSubmit: (data: CreateProjectInput) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function ProjectForm({
  domain,
  project,
  clients = [],
  onSubmit,
  onCancel,
  isLoading = false,
}: ProjectFormProps) {
  const [metadata, setMetadata] = useState<Record<string, any>>(project?.metadata || {});

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: project?.name || '',
      description: project?.description || '',
      client_id: project?.client_id || undefined,
      status: project?.status || 'planning',
      budget_revenue: project?.budget_revenue || undefined,
      budget_cost: project?.budget_cost || undefined,
      start_date: project?.start_date ? new Date(project.start_date) : undefined,
      end_date: project?.end_date ? new Date(project.end_date) : undefined,
    },
  });

  // Domain-specific metadata fields
  const getMetadataFields = () => {
    switch (domain) {
      case CompanyType.AGENCIES:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Projeto</FormLabel>
              <Select
                value={metadata.project_type || ''}
                onValueChange={(val) => setMetadata({ ...metadata, project_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="branding">Branding</SelectItem>
                  <SelectItem value="campaign">Campanha</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="app">Aplicativo</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>

            <FormItem>
              <FormLabel>Membros da Equipe</FormLabel>
              <FormControl>
                <Input
                  placeholder="João Silva, Maria Santos..."
                  value={metadata.team_members?.join(', ') || ''}
                  onChange={(e) =>
                    setMetadata({
                      ...metadata,
                      team_members: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                />
              </FormControl>
              <FormDescription>Separe os nomes por vírgula</FormDescription>
            </FormItem>
          </>
        );

      case CompanyType.CONSTRUCTION:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Obra</FormLabel>
              <Select
                value={metadata.construction_type || ''}
                onValueChange={(val) => setMetadata({ ...metadata, construction_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="renovation">Reforma</SelectItem>
                  <SelectItem value="new_construction">Obra Nova</SelectItem>
                  <SelectItem value="repair">Reparo</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>

            <FormItem>
              <FormLabel>Endereço da Obra</FormLabel>
              <FormControl>
                <Input
                  placeholder="Rua, Número - Cidade/Estado"
                  value={metadata.property_address || ''}
                  onChange={(e) => setMetadata({ ...metadata, property_address: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Área (m²)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="150"
                  value={metadata.square_meters || ''}
                  onChange={(e) =>
                    setMetadata({ ...metadata, square_meters: parseFloat(e.target.value) || undefined })
                  }
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Alvará / Número da Obra</FormLabel>
              <FormControl>
                <Input
                  placeholder="12345/2025"
                  value={metadata.building_permit_number || ''}
                  onChange={(e) => setMetadata({ ...metadata, building_permit_number: e.target.value })}
                />
              </FormControl>
            </FormItem>
          </>
        );

      case CompanyType.ARCHITECTS_ENGINEERS:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Projeto</FormLabel>
              <Select
                value={metadata.project_type || ''}
                onValueChange={(val) => setMetadata({ ...metadata, project_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="architecture">Arquitetura</SelectItem>
                  <SelectItem value="structural">Estrutural</SelectItem>
                  <SelectItem value="electrical">Elétrica</SelectItem>
                  <SelectItem value="plumbing">Hidráulica</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>

            <FormItem>
              <FormLabel>Disciplina de Engenharia</FormLabel>
              <FormControl>
                <Input
                  placeholder="Civil, Mecânica, Elétrica..."
                  value={metadata.engineering_discipline || ''}
                  onChange={(e) => setMetadata({ ...metadata, engineering_discipline: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Quantidade de Pranchas</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="10"
                  value={metadata.drawings_count || ''}
                  onChange={(e) =>
                    setMetadata({ ...metadata, drawings_count: parseInt(e.target.value) || undefined })
                  }
                />
              </FormControl>
            </FormItem>
          </>
        );

      case CompanyType.CONSULTING_B2B:
        return (
          <FormItem>
            <FormLabel>Tipo de Serviço</FormLabel>
            <FormControl>
              <Input
                placeholder="Consultoria Estratégica, Processos..."
                value={metadata.service_type || ''}
                onChange={(e) => setMetadata({ ...metadata, service_type: e.target.value })}
              />
            </FormControl>
          </FormItem>
        );

      case CompanyType.IT_COMPANIES:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Projeto</FormLabel>
              <Select
                value={metadata.project_type || ''}
                onValueChange={(val) => setMetadata({ ...metadata, project_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="development">Desenvolvimento</SelectItem>
                  <SelectItem value="support">Suporte</SelectItem>
                  <SelectItem value="infrastructure">Infraestrutura</SelectItem>
                  <SelectItem value="consulting">Consultoria</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>

            <FormItem>
              <FormLabel>Stack Tecnológico</FormLabel>
              <FormControl>
                <Input
                  placeholder="React, Node.js, PostgreSQL..."
                  value={metadata.technology_stack?.join(', ') || ''}
                  onChange={(e) =>
                    setMetadata({
                      ...metadata,
                      technology_stack: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                />
              </FormControl>
              <FormDescription>Separe as tecnologias por vírgula</FormDescription>
            </FormItem>

            <FormItem>
              <FormLabel>URL do Repositório</FormLabel>
              <FormControl>
                <Input
                  type="url"
                  placeholder="https://github.com/..."
                  value={metadata.repository_url || ''}
                  onChange={(e) => setMetadata({ ...metadata, repository_url: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Nível de SLA</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ouro, Prata, Bronze..."
                  value={metadata.sla_level || ''}
                  onChange={(e) => setMetadata({ ...metadata, sla_level: e.target.value })}
                />
              </FormControl>
            </FormItem>
          </>
        );

      case CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Evento</FormLabel>
              <Select
                value={metadata.event_type || ''}
                onValueChange={(val) => setMetadata({ ...metadata, event_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wedding">Casamento</SelectItem>
                  <SelectItem value="corporate">Corporativo</SelectItem>
                  <SelectItem value="product">Produto</SelectItem>
                  <SelectItem value="portrait">Retrato</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>

            <FormItem>
              <FormLabel>Local do Evento</FormLabel>
              <FormControl>
                <Input
                  placeholder="Nome do local ou endereço"
                  value={metadata.location || ''}
                  onChange={(e) => setMetadata({ ...metadata, location: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Estimativa de Fotos</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="500"
                  value={metadata.photo_count_estimate || ''}
                  onChange={(e) =>
                    setMetadata({ ...metadata, photo_count_estimate: parseInt(e.target.value) || undefined })
                  }
                />
              </FormControl>
            </FormItem>
          </>
        );

      case CompanyType.EVENT_PRODUCERS:
        return (
          <>
            <FormItem>
              <FormLabel>Tipo de Evento</FormLabel>
              <FormControl>
                <Input
                  placeholder="Casamento, Corporativo, Festa..."
                  value={metadata.event_type || ''}
                  onChange={(e) => setMetadata({ ...metadata, event_type: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Local do Evento</FormLabel>
              <FormControl>
                <Input
                  placeholder="Nome do local ou endereço"
                  value={metadata.location || ''}
                  onChange={(e) => setMetadata({ ...metadata, location: e.target.value })}
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Estimativa de Participantes</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="200"
                  value={metadata.attendees_estimate || ''}
                  onChange={(e) =>
                    setMetadata({ ...metadata, attendees_estimate: parseInt(e.target.value) || undefined })
                  }
                />
              </FormControl>
            </FormItem>
          </>
        );

      default:
        return null;
    }
  };

  const handleFormSubmit = async (data: ProjectFormData) => {
    const submitData: CreateProjectInput = {
      name: data.name,
      description: data.description,
      client_id: data.client_id,
      status: data.status,
      budget_revenue: data.budget_revenue,
      budget_cost: data.budget_cost,
      start_date: data.start_date?.toISOString(),
      end_date: data.end_date?.toISOString(),
      metadata,
    };

    await onSubmit(submitData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Projeto *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nome do projeto" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Descrição detalhada do projeto..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {clients.length > 0 && (
              <FormField
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(val === 'none' ? undefined : parseInt(val, 10))}
                      value={field.value?.toString() || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um cliente" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Nenhum cliente</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="planning">Planejamento</SelectItem>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="on_hold">Pausado</SelectItem>
                      <SelectItem value="completed">Concluído</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Orçamento e Datas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="budget_revenue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Receita Orçada</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="budget_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custo Orçado</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data de Início</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP', { locale: ptBR })
                            ) : (
                              <span>Selecione uma data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={ptBR}
                          disabled={(date) => date < new Date('1900-01-01')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data de Término</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP', { locale: ptBR })
                            ) : (
                              <span>Selecione uma data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={ptBR}
                          disabled={(date) => date < new Date('1900-01-01')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Informações Específicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">{getMetadataFields()}</CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {project ? 'Atualizar Projeto' : 'Criar Projeto'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
